/**
 * Theme Context - Shared theme state for the entire app
 *
 * Features:
 * - System preference detection
 * - User preference override (persisted to localStorage)
 * - All consumers share the same state
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export type ThemeMode = 'dark' | 'light';
export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_STORAGE_KEY = 'fmf-theme-preference';

interface ThemeContextValue {
  theme: ThemeMode;
  isDark: boolean;
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
  toggleTheme: () => void;
  cycleTheme: () => void;
  isLandingPage: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Get the system's preferred color scheme
 */
function getSystemTheme(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Apply theme to document and update meta theme-color for status bar
 */
function applyTheme(theme: ThemeMode) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
    document.body.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
    document.body.classList.remove('dark');
  }

  // Update theme-color meta tag for mobile status bar
  const themeColor = theme === 'dark' ? '#121212' : '#f8f9fb';
  let metaThemeColor = document.querySelector('meta[name="theme-color"]:not([media])');

  // Remove media-specific theme-color tags and use a single dynamic one
  document.querySelectorAll('meta[name="theme-color"][media]').forEach(el => el.remove());

  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta');
    metaThemeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(metaThemeColor);
  }
  metaThemeColor.setAttribute('content', themeColor);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // User's saved preference
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
    return 'system';
  });

  // Track system theme for 'system' preference
  const [systemTheme, setSystemTheme] = useState<ThemeMode>(getSystemTheme);

  // Landing page detection
  const isLandingPage = location.pathname === '/';

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Resolve actual theme based on preference
  const theme = useMemo((): ThemeMode => {
    if (preference === 'system') {
      return systemTheme;
    }
    return preference;
  }, [preference, systemTheme]);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(theme);

    if (isLandingPage) {
      document.body.classList.add('landing-page');
    } else {
      document.body.classList.remove('landing-page');
    }

    return () => {
      document.body.classList.remove('landing-page');
    };
  }, [theme, isLandingPage]);

  // Save preference
  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  }, []);

  // Quick toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setPreference(newTheme);
  }, [theme, setPreference]);

  // Cycle through system -> light -> dark
  const cycleTheme = useCallback(() => {
    const cycle: ThemePreference[] = ['system', 'light', 'dark'];
    const currentIndex = cycle.indexOf(preference);
    const nextIndex = (currentIndex + 1) % cycle.length;
    setPreference(cycle[nextIndex]);
  }, [preference, setPreference]);

  const value = useMemo(() => ({
    theme,
    isDark: theme === 'dark',
    preference,
    setPreference,
    toggleTheme,
    cycleTheme,
    isLandingPage,
  }), [theme, preference, setPreference, toggleTheme, cycleTheme, isLandingPage]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Re-export types for convenience
export type { ThemeContextValue };
